import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import type { ExpressEventCandidate } from '@/services/eventOngoingService';
import { storageManager } from '@/storage/storageManager';
import { STORAGE_KEYS } from '@/storage/storageKeys';

type EventSoundMarker = { startPlayedAt?: number; endPlayedAt?: number };
const NOTIFICATION_REPEAT_COUNT = 3;
const NOTIFICATION_REPEAT_GAP_MS = 1200;

function hashToInt32(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash || 1);
}

function getNotificationId(eventKey: string, edge: 'start' | 'end', sequence: number): number {
  const raw = hashToInt32(`${eventKey}:${edge}:${sequence}`);
  return raw % 2147483000;
}

export async function ensureEventNotificationPermission(artistKey: string): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;

  const alreadyAsked = await storageManager.get<boolean>(
    STORAGE_KEYS.EXPRESS_EVENT_NOTIFICATIONS_PERMISSION_ASKED(artistKey)
  );

  if (!alreadyAsked) {
    await storageManager.set(
      STORAGE_KEYS.EXPRESS_EVENT_NOTIFICATIONS_PERMISSION_ASKED(artistKey),
      true
    );
  }

  try {
    const permissionStatus = await LocalNotifications.checkPermissions();
    if (permissionStatus.display === 'granted') return true;

    const requestStatus = await LocalNotifications.requestPermissions();
    return requestStatus.display === 'granted';
  } catch {
    return false;
  }
}

export async function syncExpressEventNotifications(params: {
  artistKey: string;
  candidates: ExpressEventCandidate[];
  soundMarkersByEventKey: Record<string, EventSoundMarker>;
}): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  const { artistKey, candidates, soundMarkersByEventKey } = params;
  const allowed = await ensureEventNotificationPermission(artistKey);
  if (!allowed) return;

  const nowMs = Date.now();
  const notifications: Array<{
    id: number;
    title: string;
    body: string;
    schedule: { at: Date; allowWhileIdle: true };
    extra: { eventKey: string; edge: 'start' | 'end'; dealId: number };
  }> = [];
  const managedIds = new Set<number>();

  const pushRepeatedNotifications = (
    edge: 'start' | 'end',
    baseTimeMs: number,
    event: ExpressEventCandidate
  ) => {
    for (let sequence = 0; sequence < NOTIFICATION_REPEAT_COUNT; sequence += 1) {
      const id = getNotificationId(event.eventKey, edge, sequence);
      managedIds.add(id);

      const title = edge === 'start' ? 'Event started' : 'Event ended';
      const body =
        edge === 'start'
          ? 'Your express event is now ongoing.'
          : 'Your express event duration has completed.';
      const atMs = baseTimeMs + sequence * NOTIFICATION_REPEAT_GAP_MS;

      if (atMs <= nowMs) continue;

      notifications.push({
        id,
        title,
        body,
        schedule: { at: new Date(atMs), allowWhileIdle: true },
        extra: { eventKey: event.eventKey, edge, dealId: event.dealId },
      });
    }
  };

  for (const event of candidates) {
    const marker = soundMarkersByEventKey[event.eventKey] ?? {};

    if (event.startMs > nowMs && !marker.startPlayedAt) {
      pushRepeatedNotifications('start', event.startMs, event);
    }

    if (event.endMs > nowMs && !marker.endPlayedAt) {
      pushRepeatedNotifications('end', event.endMs, event);
    }
  }

  const existing = await LocalNotifications.getPending();
  const existingIds = new Set(
    existing.notifications
      .map((item) => item.id)
      .filter((id) => managedIds.has(id))
  );
  const nextIds = new Set(notifications.map((item) => item.id));
  const toCancel = [...existingIds]
    .filter((id) => !nextIds.has(id))
    .map((id) => ({ id }));

  if (toCancel.length > 0) {
    await LocalNotifications.cancel({ notifications: toCancel });
  }

  if (notifications.length > 0) {
    await LocalNotifications.schedule({ notifications });
  }
}

